import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  TrendingUp,
  Users,
  Code,
  Sparkles,
  Calendar,
  Palette,
  ClipboardCheck,
  AlertTriangle,
  ArrowRight,
  Target,
  Activity,
  BarChart3,
  Timer,
  RefreshCw,
  XCircle,
  FileText
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerDashboard = () => {
  const { token, user: authUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [insight, setInsight] = useState('');
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [users, setUsers] = useState([]);
  const [myTeam, setMyTeam] = useState(null);

  useEffect(() => {
    // Initial mount — load both data sources and seed the "last refreshed" stamp.
    handleRefresh({ initial: true });
  }, []); // eslint-disable-line

  // Cache-busting headers + query param so the proxy / browser never serves a
  // stale 304. Backend ignores unknown params.
  const buildNoCacheConfig = () => ({
    headers: {
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    params: { _ts: Date.now() },
  });

  const loadOverview = async () => {
    const response = await axios.get(`${API}/analytics/overview`, buildNoCacheConfig());
    setOverview(response.data);
  };

  const loadUsers = async () => {
    const response = await axios.get(`${API}/users`, buildNoCacheConfig());
    setUsers(response.data);
  };

  const loadMyTeam = async () => {
    try {
      const r = await axios.get(`${API}/users/my-team`, buildNoCacheConfig());
      setMyTeam(r.data);
    } catch (e) {
      // Non-fatal
    }
  };

  // Unified refresh — re-fetches ALL dashboard data in parallel, surfaces a
  // success/error toast and updates the "Last refreshed" timestamp.
  const handleRefresh = async ({ initial = false } = {}) => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([loadOverview(), loadUsers(), loadMyTeam()]);
      setLastRefreshed(new Date());
      if (!initial) toast.success('Dashboard refreshed successfully');
    } catch (error) {
      toast.error(initial ? 'Failed to load dashboard' : 'Unable to refresh dashboard. Please try again.');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const generateInsight = async () => {
    setLoadingInsight(true);
    try {
      const response = await axios.get(`${API}/analytics/generate-insight`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsight(response.data.insight);
      toast.success('AI Insight generated!');
    } catch (error) {
      toast.error('Failed to generate insight');
    } finally {
      setLoadingInsight(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.full_name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Primary metrics
  const primaryMetrics = [
    { label: 'Total Tasks', value: overview?.total_tasks || 0, icon: Calendar, color: 'from-slate-600 to-slate-700', bgColor: 'bg-slate-100' },
    { label: 'Completion Rate', value: `${overview?.completion_rate || 0}%`, icon: Target, color: 'from-emerald-500 to-green-600', bgColor: 'bg-emerald-100' },
    { label: 'Completed', value: overview?.completed || 0, icon: CheckCircle2, color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-100' },
    { label: 'In Progress', value: overview?.in_progress || 0, icon: Clock, color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-100' },
  ];

  // Alert metrics
  const alertMetrics = [
    { label: 'Overdue', value: overview?.overdue || 0, icon: AlertCircle, color: 'from-red-500 to-rose-500', bgColor: 'bg-red-100', link: '/task-table' },
    { label: 'Due Today', value: overview?.due_today || 0, icon: Timer, color: 'from-amber-500 to-orange-500', bgColor: 'bg-amber-100', link: '/task-table' },
    { label: 'Pending Approval', value: overview?.pending_approval_count || 0, icon: ClipboardCheck, color: 'from-purple-500 to-violet-500', bgColor: 'bg-purple-100', link: '/manager-approval' },
    { label: 'Rejected Tasks', value: overview?.rejected_tasks || 0, icon: XCircle, color: 'from-rose-500 to-pink-500', bgColor: 'bg-rose-100', link: '/manager-approval' },
  ];

  // Dependency metrics
  const dependencyMetrics = [
    { label: 'Dev Blocked', value: overview?.dev_blocked || 0, icon: Code, color: 'from-orange-500 to-red-500', bgColor: 'bg-orange-100', link: '/developer' },
    { label: 'UI/UX Blocked', value: overview?.uiux_blocked || 0, icon: Palette, color: 'from-pink-500 to-rose-500', bgColor: 'bg-pink-100', link: '/design-queue' },
    { label: 'Yet to Start', value: overview?.yet_to_start || 0, icon: TrendingUp, color: 'from-slate-400 to-slate-500', bgColor: 'bg-slate-100', link: '/task-table' },
    { label: 'On Hold', value: overview?.on_hold || 0, icon: AlertTriangle, color: 'from-yellow-500 to-amber-500', bgColor: 'bg-yellow-100', link: '/task-table' },
  ];

  // Chart data
  const statusData = Object.entries(overview?.status_breakdown || {}).map(([name, value]) => ({
    name,
    value
  }));

  const categoryData = Object.entries(overview?.categories || {}).map(([name, value]) => ({
    name,
    value
  }));

  const weeklyTrendData = overview?.weekly_trend || [];

  const STATUS_COLORS = {
    'Backlog': '#64748b',
    'Completed': '#10b981',
    'In Process': '#3b82f6',
    'Yet to start': '#f59e0b',
    'On Hold': '#ef4444'
  };

  const CATEGORY_COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#f43f5e'];

  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      <div className="page-header">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="page-title">Manager Dashboard</h1>
            <p className="page-subtitle">Comprehensive overview of team performance and task progress</p>
            {lastRefreshed && (
              <p
                className="text-xs text-slate-500 mt-1"
                data-testid="last-refreshed-timestamp"
                title={lastRefreshed.toISOString()}
              >
                Last refreshed: {format(lastRefreshed, 'MMM d, yyyy • h:mm:ss a')}
              </p>
            )}
          </div>
          <Button
            onClick={() => handleRefresh()}
            variant="outline"
            disabled={refreshing}
            data-testid="dashboard-refresh-button"
            aria-busy={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw
              size={16}
              className={refreshing ? 'animate-spin' : ''}
            />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* My Team card — visible when the current user has direct reports (Team Leads) */}
      {myTeam && myTeam.summary?.total_members > 0 && (
        <div
          className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200 p-5"
          data-testid="my-team-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Users size={22} className="text-cyan-600" />
              My Team
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({myTeam.summary.total_members} member{myTeam.summary.total_members !== 1 ? 's' : ''})
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
            {[
              { label: 'Total Members', value: myTeam.summary.total_members, color: 'text-slate-800' },
              { label: 'Active Members', value: myTeam.summary.active_members, color: 'text-emerald-600' },
              { label: 'Total Tasks', value: myTeam.summary.total_tasks, color: 'text-slate-800' },
              { label: 'Completed', value: myTeam.summary.completed, color: 'text-green-600' },
              { label: 'In Progress', value: myTeam.summary.in_progress, color: 'text-blue-600' },
              { label: 'Pending Approval', value: myTeam.summary.pending_approval, color: 'text-purple-600' },
              { label: 'Overdue', value: myTeam.summary.overdue, color: 'text-red-600' },
              { label: 'Completion Rate', value: `${myTeam.summary.completion_rate}%`, color: 'text-cyan-700' },
            ].map(k => (
              <div key={k.label} className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k.label}</p>
                <p className={`text-xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {myTeam.members.map(m => (
              <div
                key={m.id}
                className="bg-white rounded-lg border border-slate-200 p-3"
                data-testid={`team-member-card-${m.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full btn-cta flex items-center justify-center text-white text-sm font-semibold overflow-hidden">
                    {m.profile_picture ? (
                      <img src={m.profile_picture} alt={m.full_name} className="w-full h-full object-cover" />
                    ) : (
                      (m.full_name || '?').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{m.full_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      Workload: <span className="font-semibold text-slate-700">{m.workload}</span>
                    </p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${m.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {m.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-sm font-bold text-green-600">{m.completed}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Done</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-blue-600">{m.pending}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Pending</p>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${m.overdue > 0 ? 'text-red-600' : 'text-slate-500'}`}>{m.overdue}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Overdue</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Primary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {primaryMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{metric.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{metric.value}</p>
                </div>
                <div className={`p-2.5 rounded-lg bg-gradient-to-br ${metric.color} text-white`}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Alert & Action Required Section */}
      <div className="bg-gradient-to-r from-red-50 via-amber-50 to-purple-50 rounded-xl p-4 border border-red-100">
        <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="text-amber-500" size={20} />
          Action Required
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {alertMetrics.map((metric) => {
            const Icon = metric.icon;
            const hasIssues = metric.value > 0;
            return (
              <Link
                key={metric.label}
                to={metric.link}
                className={`bg-white rounded-lg p-3 border ${hasIssues ? 'border-red-200 hover:border-red-300' : 'border-slate-200'} hover:shadow-md transition-all group`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500">{metric.label}</p>
                    <p className={`text-xl font-bold ${hasIssues ? 'text-red-600' : 'text-slate-700'}`}>{metric.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <Icon size={16} className={hasIssues ? 'text-red-600' : 'text-slate-500'} />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  View details <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Dependencies Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Activity className="text-cyan-500" size={20} />
          Task Status & Dependencies
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {dependencyMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Link
                key={metric.label}
                to={metric.link}
                className="bg-slate-50 rounded-lg p-3 hover:bg-slate-100 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500">{metric.label}</p>
                    <p className="text-xl font-bold text-slate-800">{metric.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <Icon size={16} className="text-slate-600" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Distribution Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Status Distribution</h2>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || '#8884d8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-12">No data available</p>
          )}
        </div>

        {/* Weekly Trend Line Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Completion Trend (7 Days)</h2>
          {weeklyTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={weeklyTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                <Tooltip />
                <Area type="monotone" dataKey="completed" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-12">No trend data available</p>
          )}
        </div>

        {/* Category Distribution Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Category Breakdown</h2>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#64748b" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} stroke="#64748b" />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-12">No category data available</p>
          )}
        </div>
      </div>

      {/* Top Content Types Widget */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
        data-testid="dashboard-content-types"
      >
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileText size={20} className="text-cyan-500" />
            Top Content Types
          </h2>
          <Link to="/reports" className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
            See all <ArrowRight size={12} />
          </Link>
        </div>
        {Object.keys(overview?.content_types || {}).length === 0 ? (
          <p className="text-sm text-slate-500 italic py-4">
            No content type data yet. Set a content type on tasks to see analytics here.
          </p>
        ) : (
          <div className="space-y-3">
            {Object.entries(overview?.content_types || {})
              .sort((a, b) => (b[1].total || 0) - (a[1].total || 0))
              .slice(0, 5)
              .map(([ct, stats]) => {
                const rate = stats.completion_rate || 0;
                const rateColor =
                  rate >= 70 ? 'bg-emerald-500' : rate >= 40 ? 'bg-amber-500' : 'bg-rose-500';
                const textColor =
                  rate >= 70 ? 'text-emerald-700' : rate >= 40 ? 'text-amber-700' : 'text-rose-700';
                return (
                  <div key={ct} data-testid={`dashboard-ct-${ct}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{ct}</span>
                      <span className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-800">{stats.total}</span>
                        {' tasks · '}
                        <span className={`font-semibold ${textColor}`}>{rate}%</span>
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${rateColor} transition-all`}
                        style={{ width: `${rate}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Dependency Queues */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Developer Queue Status */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Code className="text-orange-500" size={20} />
              Developer Queue
            </h2>
            <Link to="/developer" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Tasks</span>
              <span className="font-bold text-slate-800">{overview?.dev_queue?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm text-slate-600">Pending</span>
              <span className="font-bold text-yellow-600">{overview?.dev_queue?.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-slate-600">In Development</span>
              <span className="font-bold text-blue-600">{overview?.dev_queue?.in_development || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-slate-600">Completed</span>
              <span className="font-bold text-green-600">{overview?.dev_queue?.completed || 0}</span>
            </div>
          </div>
        </div>

        {/* UI/UX Queue Status */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Palette className="text-purple-500" size={20} />
              UI/UX Design Queue
            </h2>
            <Link to="/design-queue" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Tasks</span>
              <span className="font-bold text-slate-800">{overview?.uiux_queue?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm text-slate-600">Pending</span>
              <span className="font-bold text-yellow-600">{overview?.uiux_queue?.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-sm text-slate-600">In Progress</span>
              <span className="font-bold text-purple-600">{overview?.uiux_queue?.in_progress || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-slate-600">Completed</span>
              <span className="font-bold text-green-600">{overview?.uiux_queue?.completed || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Approvals & Overdue Tasks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="text-purple-500" size={20} />
              Pending Approvals
            </h2>
            <Link to="/manager-approval" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              Review all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {(overview?.pending_approvals || []).length > 0 ? (
              overview.pending_approvals.map((task) => (
                <div key={task.id} className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="font-medium text-slate-800 text-sm truncate">{task.task}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-slate-500">{getUserName(task.task_done_by)}</span>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                      {task.category}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-slate-500 text-sm">No pending approvals!</p>
              </div>
            )}
          </div>
        </div>

        {/* Overdue Tasks */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertCircle className="text-red-500" size={20} />
              Overdue Tasks
            </h2>
            <Link to="/task-table" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {(overview?.overdue_tasks || []).length > 0 ? (
              overview.overdue_tasks.map((task) => (
                <div key={task.id} className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="font-medium text-slate-800 text-sm truncate">{task.task}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-slate-500">{getUserName(task.task_done_by)}</span>
                    <span className="text-xs text-red-600 font-medium">
                      Due: {task.your_end_date ? format(new Date(task.your_end_date), 'MMM dd') : 'N/A'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-slate-500 text-sm">No overdue tasks!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Users className="text-cyan-500" size={20} />
            Team Performance
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(overview?.users || {})
            .sort(([, a], [, b]) => b.total - a.total)
            .slice(0, 9)
            .map(([userId, data]) => {
              const completion = data.completion_rate || 0;
              const userName = data.name || `User #${userId.slice(0, 8)}`;
              const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
              const hasOverdue = data.overdue > 0;
              
              return (
                <div key={userId} className={`p-4 rounded-lg border ${hasOverdue ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{userName}</p>
                      <p className="text-xs text-slate-500">{data.role || 'Team Member'}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">Progress</span>
                      <span className="font-semibold text-slate-800">{data.completed}/{data.total}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${completion >= 70 ? 'bg-green-500' : completion >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${completion}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className={`font-semibold ${completion >= 70 ? 'text-green-600' : completion >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        {completion}% complete
                      </span>
                      {hasOverdue && (
                        <span className="text-red-600 font-semibold flex items-center gap-1">
                          <AlertCircle size={12} /> {data.overdue} overdue
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200 mt-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{data.in_progress}</p>
                        <p className="text-xs text-slate-500">Active</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-500">{data.yet_to_start}</p>
                        <p className="text-xs text-slate-500">Pending</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{data.completed}</p>
                        <p className="text-xs text-slate-500">Done</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          {Object.keys(overview?.users || {}).length === 0 && (
            <p className="col-span-full text-center text-slate-500 py-8">No team data available</p>
          )}
        </div>
      </div>

      {/* AI Insight */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="text-cyan-500" size={20} />
            AI-Powered Insights
          </h2>
          <Button
            onClick={generateInsight}
            disabled={loadingInsight}
            data-testid="generate-insight-button"
            size="sm"
            className="btn-cta"
          >
            <RefreshCw size={14} className={`mr-2 ${loadingInsight ? 'animate-spin' : ''}`} />
            {loadingInsight ? 'Analyzing...' : 'Generate Insight'}
          </Button>
        </div>
        
        {insight && (
          <div className="space-y-4" data-testid="ai-insight-content">
            {/* Parse and render insight sections */}
            {insight.split('##').filter(Boolean).map((section, idx) => {
              const lines = section.trim().split('\n');
              const title = lines[0]?.trim();
              const content = lines.slice(1).join('\n').trim();
              
              // Determine section styling based on title content
              let bgColor = 'bg-slate-50';
              let borderColor = 'border-slate-200';
              let iconColor = 'text-slate-500';
              
              if (title?.includes('Key Highlight') || title?.includes('🔥')) {
                bgColor = 'bg-gradient-to-r from-orange-50 to-amber-50';
                borderColor = 'border-orange-200';
                iconColor = 'text-orange-500';
              } else if (title?.includes('Action Items') || title?.includes('⚡')) {
                bgColor = 'bg-gradient-to-r from-blue-50 to-cyan-50';
                borderColor = 'border-blue-200';
                iconColor = 'text-blue-500';
              } else if (title?.includes('Optimization') || title?.includes('💡')) {
                bgColor = 'bg-gradient-to-r from-yellow-50 to-lime-50';
                borderColor = 'border-yellow-200';
                iconColor = 'text-yellow-600';
              } else if (title?.includes('Week Ahead') || title?.includes('📅')) {
                bgColor = 'bg-gradient-to-r from-purple-50 to-pink-50';
                borderColor = 'border-purple-200';
                iconColor = 'text-purple-500';
              }
              
              return title && content && (
                <div 
                  key={idx} 
                  className={`${bgColor} border ${borderColor} rounded-lg p-4 transition-all hover:shadow-sm`}
                >
                  <h3 className={`font-semibold text-slate-800 mb-2 flex items-center gap-2`}>
                    {title}
                  </h3>
                  <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {content}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!insight && !loadingInsight && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 flex items-center justify-center">
              <Sparkles className="text-cyan-500" size={28} />
            </div>
            <p className="text-slate-600 font-medium mb-2">Get AI-Powered Analysis</p>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Click &ldquo;Generate Insight&rdquo; to receive personalized recommendations based on your team&rsquo;s performance data, queue status, and workflow patterns.
            </p>
          </div>
        )}
        
        {loadingInsight && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 flex items-center justify-center animate-pulse">
              <Sparkles className="text-cyan-500 animate-spin" size={28} />
            </div>
            <p className="text-slate-600 font-medium mb-2">Analyzing Your Data...</p>
            <p className="text-slate-500 text-sm">
              AI is reviewing task queues, approvals, and team metrics
            </p>
          </div>
        )}
      </div>

      {/* Recently Completed */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <CheckCircle2 className="text-green-500" size={20} />
            Recently Completed (Last 7 Days)
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(overview?.recent_completed || []).length > 0 ? (
            overview.recent_completed.map((task) => (
              <div key={task.id} className="p-3 bg-green-50 rounded-lg border border-green-100">
                <p className="font-medium text-slate-800 text-sm truncate">{task.task}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-500">{getUserName(task.task_done_by)}</span>
                  <span className="text-xs text-green-600">
                    {task.completed_date ? format(new Date(task.completed_date), 'MMM dd') : 'N/A'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="col-span-full text-center text-slate-500 py-6">No tasks completed in the last 7 days</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
